import { AdvancedSelect } from '../../widgets/select';
import { Dialog, useDialogManager } from '../../widgets/dialog';
import { FormControl } from '../../widgets/formControl';
import { Icon } from '../../widgets/icon';
import { Link } from '../../widgets/buttons';
import { NotificationBanner } from '../../widgets/notificationBanner';
import { showNotification } from '../../widgets/notificationToaster';
import React, { CSSProperties, FC, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { UpgradeDialog } from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { useAppState } from 'src/shared/appState';
import { T, stringFormat } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { AddSalesChannelsDialogWindow } from '../../../shared/connectDialogStyle';
import { ConnectShopDialog } from '../../../shared/connectShopDialog';
import { CustomDialogWrapper } from '../../../shared/customDialogWrapper';
import { MarketplaceConnectShopDialog } from '../../../shared/marketplaceConnectShopDialog';
import ExternalLinkAltSolid from '../../../shared/svg/externalLinkAltSolid';
import { ZendeskShopDialog } from '../../../shared/zendeskShopDialog';
import { addSalesChannelDialogQuery } from './__generated__/addSalesChannelDialogQuery.graphql';
import BlankApiIntegrationSalesChannelDialog from './blankApiIntegrationSalesChannelDialog';
import CCloudIntegrationSalesChannelDialog from './cCloudIntegrationSalesChannelDialog';
import { salesChannelList } from './helpers';

const TitleBanner = styled.p`
    margin: 0px;
`;

const SubTileBanner = styled.p`
    margin-top: 14px;
    margin-bottom: 0px;
`;

const UlBanner = styled.ul`
    margin: 0px;
    padding-left: 20px;
`;

const LiBanner = styled.li`
    margin-top: 8px;
`;

const FormContainer = styled.div`
    padding: 32px 0px 12px;
`;

const StyledDivDialog = styled.div`
    h5 {
        font-size: 20px;
        justify-content: left;
        padding-bottom: 8px;
        margin: 0px;
    }
    text-align: left;
    p {
        font-size: 14px;
        margin: 0px;
        margin-bottom: 32px;
    }
`;

const IconContainer = styled(Icon)`
    vertical-align: bottom;
`;

const UpBannerSection = styled.div`
    margin: 24px 0 32px;
`;

interface DialogInfoSectionProps {
    title: string;
    description?: string | React.ReactNode | undefined;
    learnMoreText?: string | undefined;
    learnMoreLink?: string | undefined;
    customDivStyle?: CSSProperties | undefined;
}

interface AddSalesChannelDialogProps {
    onSalesChannelCreated: () => void;
}

export const DialogInfoSection: React.FC<DialogInfoSectionProps> = ({ title, description, learnMoreText, learnMoreLink, customDivStyle }) => {
    return (
        <StyledDivDialog style={customDivStyle}>
            <h5>{T._(title, 'Admin')}</h5>
            {description && (
                <p>
                    {description}
                    {learnMoreLink && (
                        <Link href={learnMoreLink} target='_blank'>
                            {learnMoreText}
                        </Link>
                    )}
                </p>
            )}
        </StyledDivDialog>
    );
};

const AddSalesChannelDialog: FC<AddSalesChannelDialogProps> = ({ onSalesChannelCreated }) => {
    const [chosenSalesChannel, setChosenSalesChannel] = useState<string | null>('');
    const { showDialog, closeDialog } = useDialogManager();
    const { sellerMarketplace, subscription, updateSubscription } = useSubscriptionData();
    const { state } = useAppState();

    const query = useLazyLoadQuery<addSalesChannelDialogQuery>(
        graphql`
            query addSalesChannelDialogQuery {
                ecommerceSettings {
                    ecommerceID
                    ecommerceName
                    remoteUrl
                }

                planForUpgradeSeller {
                    salesChannelPrice
                }

                hasUsedPromotion(promotion: FREE_COST_SALES_CHANNEL)
            }
        `,
        {}
    );

    const scPriceFormatted = state.priceFormatter.format(
        (subscription?.plan?.salesChannelPrice && subscription?.plan?.salesChannelPrice !== -1
            ? subscription?.plan?.salesChannelPrice
            : query.planForUpgradeSeller?.salesChannelPrice) || -1
    );

    const isShopifyMaster = query.ecommerceSettings?.ecommerceName === 'SHOPIFY';

    const updatedSalesChannelList = salesChannelList
        .filter((option) => sellerMarketplace !== 'MICROSOFT_AZURE' || (option.value !== 'SHOPIFY' && option.value !== 'WIX'))
        .map((option) => {
            if (option.value === 'SHOPIFY' && !isShopifyMaster) {
                return { ...option, label: `${option.label} (not available)`, isDisabled: true };
            } else if (option.value === 'COMMERCE_CLOUD' || option.value === 'WIX' || option.value === 'SHOP_WIRED') {
                return { ...option, label: `${option.label} (coming soon)`, isDisabled: true };
            } else {
                return option;
            }
        });

    const handleConfirmClick = (platform: string) => {
        const platformToShow = salesChannelList.find((x) => x.value.toLowerCase() === platform.toLowerCase());

        if (!platformToShow) {
            showNotification({
                type: 'danger',
                message: T._('Select one Sales channel to add', 'Admin'),
                dismiss: {
                    duration: 5000
                }
            });
        } else {
            if (platformToShow.value === 'API') {
                showDialog(
                    'api-connection',
                    <CustomDialogWrapper showCloseButton={false} windowDecorator={AddSalesChannelsDialogWindow} size='large'>
                        <BlankApiIntegrationSalesChannelDialog
                            windowDecorator={AddSalesChannelsDialogWindow}
                            hasMasterIntegration={true}
                            onSalesChannelCreated={() => {
                                closeDialog('api-connection');
                                onSalesChannelCreated();
                            }}
                            title={'Connect via APIs'}
                        ></BlankApiIntegrationSalesChannelDialog>
                    </CustomDialogWrapper>
                );
            } else if (platformToShow.value === 'COMMERCE_CLOUD') {
                showDialog(
                    'sales-force',
                    <CustomDialogWrapper windowDecorator={AddSalesChannelsDialogWindow} size='large' showCloseButton={false}>
                        <CCloudIntegrationSalesChannelDialog
                            windowDecorator={AddSalesChannelsDialogWindow}
                            title={T._('Connect to SalesForce Commerce Cloud', 'Admin')}
                        />
                    </CustomDialogWrapper>
                );
            } else if (platformToShow.value === 'SHOP_WIRED') {
                showDialog(
                    'zendesk-connect',
                    <CustomDialogWrapper showCloseButton={false} windowDecorator={AddSalesChannelsDialogWindow} size='large'>
                        <ZendeskShopDialog
                            platform={platform}
                            title={T._(`Installation instructions [${platform}]`, 'Admin')}
                            windowDecorator={AddSalesChannelsDialogWindow}
                            size='large'
                        />
                    </CustomDialogWrapper>
                );
            } else if (platformToShow.value === 'AMAZON' || platformToShow.value === 'ETSY') {
                showDialog(
                    'marketplace-connect',
                    <CustomDialogWrapper showCloseButton={false} windowDecorator={AddSalesChannelsDialogWindow} size='large'>
                        {' '}
                        <MarketplaceConnectShopDialog
                            titleHtml={
                                <>
                                    <h2>
                                        {platformToShow.titleDialog}
                                        <IconContainer
                                            onClick={() => window.open(`https://zakeke.zendesk.com/hc/en-us/articles/${platformToShow.zendeskDocId}`, '_blank')}
                                        >
                                            <ExternalLinkAltSolid />
                                        </IconContainer>
                                    </h2>
                                </>
                            }
                            platform={platform}
                            windowDecorator={AddSalesChannelsDialogWindow}
                            size='large'
                        />
                    </CustomDialogWrapper>
                );
            } else if (platformToShow.zendeskDocId) {
                showDialog(
                    'docs',
                    <CustomDialogWrapper showCloseButton={false} windowDecorator={AddSalesChannelsDialogWindow} size='large'>
                        {' '}
                        <ConnectShopDialog
                            title={platformToShow.titleDialog}
                            id={platformToShow.zendeskDocId}
                            windowDecorator={AddSalesChannelsDialogWindow}
                            size='large'
                        />
                    </CustomDialogWrapper>
                );
            }
        }
    };

    return (
        <Dialog
            windowDecorator={AddSalesChannelsDialogWindow}
            size='large'
            buttons={[
                {
                    label: T._('Select', 'Admin'),
                    onClick: () => {
                        handleConfirmClick(chosenSalesChannel!);
                    },
                    disabled: !subscription?.areSalesChannelsEnabled
                }
            ]}
            onClose={() => closeDialog('add-sales-channel')}
        >
            <DialogInfoSection title={'Add a sales channel'} />

            {subscription?.plan?.version === 1 && (
                <UpBannerSection>
                    <NotificationBanner
                        type={'EXTRA'}
                        content={
                            !subscription?.areSalesChannelsEnabled ? (
                                <React.Fragment>
                                    <span>
                                        {T._(
                                            'Your current plan does not include the ability to connect and manage multiple Sales Channels to your Zakeke account.',
                                            'Admin'
                                        )}
                                    </span>
                                    <br />
                                    <span>{T._('If you want to add Sales Channels, please contact us at: ', 'Admin')}</span>{' '}
                                    <Link href='mailto: help@zakeke.com'>help@zakeke.com</Link>.
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <span>{T._('Remember: ', 'Admin')}</span>
                                    <span>
                                        {T._(
                                            `you will pay an extra monthly fee of ${scPriceFormatted} for each sales channel you add to your Zakeke account.`,
                                            'Admin'
                                        )}
                                    </span>
                                    {query.hasUsedPromotion === false && (
                                        <>
                                            {' '}
                                            <strong>{T._('The first month for the first sales channel added is free.', 'Admin')}</strong>
                                        </>
                                    )}
                                </React.Fragment>
                            )
                        }
                    ></NotificationBanner>
                </UpBannerSection>
            )}

            {!scPriceFormatted.includes('-1') && subscription?.plan?.version === 2 && (
                <UpBannerSection>
                    {!subscription?.areSalesChannelsEnabled && !(subscription?.plan?.isCustom ?? false) ? (
                        <UpgradeDialog
                            type={'BANNER'}
                            marketPlace={sellerMarketplace ?? undefined}
                            message={
                                sellerMarketplace === 'MICROSOFT_AZURE' ? (
                                    <>
                                        <TitleBanner>
                                            <strong>{T._('Upgrade your plan to add a Sales Channel', 'Admin')}</strong>
                                        </TitleBanner>
                                        <SubTileBanner>
                                            {stringFormat(
                                                T._(
                                                    'Your current plan does not include the ability to connect and manage multiple Sales Channels to your Zakeke account. Upgrade to the {0} Plan, from Zakeke app Saas on Azure portal, to be able to access it.',
                                                    'Admin'
                                                ),
                                                'Grow'
                                            )}
                                        </SubTileBanner>
                                    </>
                                ) : query.ecommerceSettings?.ecommerceName === 'SHOPIFY' ? (
                                    <>
                                        {stringFormat(
                                            T._(
                                                `Your current plan does not include the ability to connect and manage multiple Sales Channels to your Zakeke account. Upgrade to the {0} Plan to be able to access it. Once clicked on “Upgrade Plan” you'll be redirected to the Shopify page for plan approval. You'll pay an extra monthly fee of ${scPriceFormatted} for each sales channel.`,
                                                'Admin'
                                            ),
                                            'Grow'
                                        )}
                                        {query.hasUsedPromotion === false && (
                                            <>
                                                {' '}
                                                <strong>{T._('The first month for the first sales channel added is free.', 'Admin')}</strong>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {stringFormat(
                                            T._(
                                                `Your current plan does not include the ability to connect and manage multiple Sales Channels to your Zakeke account. Upgrade to the {0} Plan to be able to access it. Once you'll click on “Upgrade Plan” you'll be automatically upgraded to the {1} Plan. You'll pay an extra monthly fee of ${scPriceFormatted} for each sales channel.`,
                                                'Admin'
                                            ),
                                            'Grow',
                                            'Grow'
                                        )}
                                        {query.hasUsedPromotion === false && (
                                            <>
                                                {' '}
                                                <strong>{T._('The first month for the first sales channel added is free.', 'Admin')}</strong>
                                            </>
                                        )}
                                    </>
                                )
                            }
                            redirectUrlFromDashboard={window.location.href.split('?')[0] + '?showAddSalesChannelDialog=true'}
                            onUpgradeCompleted={async () => {
                                closeDialog('add-sales-channel');
                                updateSubscription();
                                return showDialog(
                                    'add-sales-channel',
                                    <AddSalesChannelDialog
                                        onSalesChannelCreated={() => {
                                            closeDialog('add-sales-channel');
                                        }}
                                    ></AddSalesChannelDialog>
                                );
                            }}
                        />
                    ) : (
                        subscription!.plan?.salesChannelPrice &&
                        subscription!.plan?.salesChannelPrice > 0 && (
                            <React.Fragment>
                                <NotificationBanner
                                    type={'EXTRA'}
                                    content={
                                        <>
                                            <span>
                                                <strong>{T._('Remember: ', 'Admin')}</strong>
                                                {T._(
                                                    `you will pay an extra monthly fee of ${scPriceFormatted} for each sales channel you add to your Zakeke account.`,
                                                    'Admin'
                                                )}
                                                {query.hasUsedPromotion === false && (
                                                    <>
                                                        {' '}
                                                        <strong>{T._('The first month for the first sales channel added is free.', 'Admin')}</strong>
                                                    </>
                                                )}
                                            </span>
                                        </>
                                    }
                                    buttons={[
                                        {
                                            label: T._('Learn more', 'Admin'),
                                            linkButton: true,
                                            href: 'https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels#h_01G5S87D64M9YYG1BZS1WWTHKQ'
                                        }
                                    ]}
                                ></NotificationBanner>
                            </React.Fragment>
                        )
                    )}
                </UpBannerSection>
            )}

            <NotificationBanner
                type={'INFO'}
                content={
                    <>
                        <TitleBanner>
                            <strong>{T._('Please note:', 'Admin')}</strong>
                        </TitleBanner>
                        <UlBanner>
                            <LiBanner>
                                {T._(
                                    'You can add a Sales Channel ONLY on the Visual Product Customizer. It DOESN’T work with the 3D Product Configurator.',
                                    'Admin'
                                )}
                            </LiBanner>
                            {sellerMarketplace !== 'MICROSOFT_AZURE' && (
                                <LiBanner>
                                    {T._('You can add a Shopify store as a sales channel ONLY IF your main integration is also Shopify.', 'Admin')}
                                </LiBanner>
                            )}
                            {sellerMarketplace !== 'MICROSOFT_AZURE' ? (
                                <LiBanner>
                                    {T._(
                                        "You CAN'T add a Wix, ShopWired, Shopware or Salesforce Commerce Cloud store as Sales Channel, they can only work as master integration.",
                                        'Admin'
                                    )}{' '}
                                </LiBanner>
                            ) : (
                                <LiBanner>
                                    {T._(
                                        "You CAN'T add ShopWired, Shopware or Salesforce Commerce Cloud store as Sales Channel, they can only work as master integration.",
                                        'Admin'
                                    )}{' '}
                                </LiBanner>
                            )}
                            <LiBanner>
                                {T._('Sales Channels and ', 'Admin')}
                                <Link
                                    href={'https://zakeke.zendesk.com/hc/en-us/sections/360004775860-Print-on-Demand-and-Dropshipping-Services'}
                                    target={'_blank'}
                                >
                                    {T._("POD's integrations", 'Admin')}
                                </Link>
                                {T._(
                                    ' ARE NOT compatible. This means that your POD catalog is only available for your master integration, not for any other extra Sales Channel you might add.',
                                    'Admin'
                                )}
                            </LiBanner>
                        </UlBanner>
                    </>
                }
            />
            <FormContainer>
                <FormControl
                    label={T._("Please choose the ecommerce platform of the store you'd like to add as extra sales channel to your Zakeke account", 'Admin')}
                >
                    <AdvancedSelect
                        isClearable
                        placeholder={T._('Choose here', 'Admin')}
                        options={updatedSalesChannelList}
                        value={updatedSalesChannelList.find((x) => x.value === chosenSalesChannel)}
                        onChange={(option) => {
                            setChosenSalesChannel(option ? option.value : '');
                        }}
                        isOptionDisabled={(option) => option.isDisabled || option.cantBeAddedAsAdditional}
                        styles={{
                            option: (provided, state) => ({
                                ...provided,
                                color: state.isDisabled ? '#AAA' : provided.color
                            })
                        }}
                        menuPosition={'fixed'}
                        isDisabled={!subscription?.areSalesChannelsEnabled}
                    />
                </FormControl>
            </FormContainer>
        </Dialog>
    );
};

export default AddSalesChannelDialog;
